import { type Token, wordTokens } from "@/lib/tool-kit/text-analysis/segment";
import { STOPWORDS } from "@/lib/tool-kit/text-analysis/stopwords";

/**
 * Keyword in context: every occurrence of a word or phrase, with a fixed number of
 * words either side, lined up on the keyword — the display corpus linguists have used
 * since Luhn's 1960 KWIC indexes, and the core view of tools like AntConc.
 *
 * Search syntax is deliberately small: words and phrases, `*` for "any letters" (so
 * `analy*` finds analyse, analysis, analytical), case sensitivity and whole-word
 * matching as switches.
 */

export interface ConcordanceOptions {
  caseSensitive: boolean;
  wholeWord: boolean;
  /** Words of context on each side. */
  width: number;
}

export interface ConcordanceLine {
  /** The match, as offsets into the text. */
  start: number;
  end: number;
  keyword: string;
  left: string;
  right: string;
  /** Context words nearest-first on the left, in reading order on the right. */
  leftWords: string[];
  rightWords: string[];
}

export type ConcordanceSort = "position" | "left" | "right" | "keyword";

export interface Collocate {
  word: string;
  left: number;
  right: number;
  total: number;
}

/** Lines shown at most; frequency and collocates still use every match. */
export const MAX_LINES = 500;

const REGEX_SPECIAL_RE = /[.+?^${}()|[\]\\]/g;
const WILDCARD_RE = /\*/g;
const SPACE_RUN_RE = /\s+/g;
const HAS_WORD_CHAR_RE = /[\p{L}\p{N}]/u;

/** The search pattern, or null when the query has no letters or digits. */
export function buildPattern(
  query: string,
  options: Pick<ConcordanceOptions, "caseSensitive" | "wholeWord">
): RegExp | null {
  const trimmed = query.trim();
  if (!HAS_WORD_CHAR_RE.test(trimmed)) {
    return null;
  }
  const body = trimmed
    .replace(REGEX_SPECIAL_RE, "\\$&")
    .replace(WILDCARD_RE, "[\\p{L}\\p{N}'’-]*")
    .replace(SPACE_RUN_RE, "\\s+");
  const source = options.wholeWord
    ? `(?<![\\p{L}\\p{N}])${body}(?![\\p{L}\\p{N}])`
    : body;
  return new RegExp(source, options.caseSensitive ? "gu" : "giu");
}

/** Index of the first token that starts at or after `offset`. */
function firstTokenFrom(tokens: Token[], offset: number): number {
  let lo = 0;
  let hi = tokens.length;
  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2);
    if (tokens[mid].start < offset) {
      lo = mid + 1;
    } else {
      hi = mid;
    }
  }
  return lo;
}

/** Index just past the last token that ends at or before `offset`. */
function tokensEndingBy(tokens: Token[], offset: number): number {
  let lo = 0;
  let hi = tokens.length;
  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2);
    if (tokens[mid].end <= offset) {
      lo = mid + 1;
    } else {
      hi = mid;
    }
  }
  return lo;
}

function flat(text: string): string {
  return text.replace(SPACE_RUN_RE, " ").trim();
}

export interface ConcordanceResult {
  lines: ConcordanceLine[];
  /** Words in the whole text, for the per-1,000-words rate. */
  totalWords: number;
}

export function concordance(
  text: string,
  query: string,
  options: ConcordanceOptions
): ConcordanceResult {
  const tokens = wordTokens(text);
  const pattern = buildPattern(query, options);
  if (!pattern) {
    return { lines: [], totalWords: tokens.length };
  }
  const width = Math.max(0, Math.round(options.width));
  const lines: ConcordanceLine[] = [];
  for (const match of text.matchAll(pattern)) {
    if (match[0].length === 0) {
      continue;
    }
    const start = match.index;
    const end = start + match[0].length;
    const leftEnd = tokensEndingBy(tokens, start);
    const leftTokens = tokens.slice(Math.max(0, leftEnd - width), leftEnd);
    const rightStart = firstTokenFrom(tokens, end);
    const rightTokens = tokens.slice(rightStart, rightStart + width);
    const leftFrom = leftTokens[0]?.start ?? start;
    const rightTo = rightTokens.at(-1)?.end ?? end;
    lines.push({
      start,
      end,
      keyword: match[0],
      left: flat(text.slice(leftFrom, start)),
      right: flat(text.slice(end, rightTo)),
      leftWords: leftTokens.map((token) => token.text).reverse(),
      rightWords: rightTokens.map((token) => token.text),
    });
  }
  return { lines, totalWords: tokens.length };
}

function compareWords(a: string[], b: string[]): number {
  const length = Math.max(a.length, b.length);
  for (let i = 0; i < length; i++) {
    const order = (a[i] ?? "").localeCompare(b[i] ?? "", "en", {
      sensitivity: "base",
    });
    if (order !== 0) {
      return order;
    }
  }
  return 0;
}

/**
 * Sort the lines. Left sorts on the word just before the keyword, then the one before
 * that; right sorts on the word just after — which is what groups the recurring
 * patterns ("the role of", "a role in") together.
 */
export function sortLines(
  lines: readonly ConcordanceLine[],
  sort: ConcordanceSort
): ConcordanceLine[] {
  const sorted = [...lines];
  if (sort === "left") {
    sorted.sort(
      (a, b) => compareWords(a.leftWords, b.leftWords) || a.start - b.start
    );
  } else if (sort === "right") {
    sorted.sort(
      (a, b) => compareWords(a.rightWords, b.rightWords) || a.start - b.start
    );
  } else if (sort === "keyword") {
    sorted.sort(
      (a, b) =>
        compareWords([a.keyword], [b.keyword]) ||
        compareWords(a.rightWords, b.rightWords) ||
        a.start - b.start
    );
  } else {
    sorted.sort((a, b) => a.start - b.start);
  }
  return sorted;
}

/** The forms a wildcard or case-insensitive search matched, most frequent first. */
export function keywordForms(
  lines: readonly ConcordanceLine[]
): { form: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const line of lines) {
    const form = line.keyword.toLowerCase().replace(SPACE_RUN_RE, " ");
    counts.set(form, (counts.get(form) ?? 0) + 1);
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([form, count]) => ({ form, count }));
}

/**
 * Words that keep company with the keyword: counted inside the context window on each
 * side, lowercased. The keyword's own forms are left out.
 */
export function collocates(
  lines: readonly ConcordanceLine[],
  excludeStopwords: boolean
): Collocate[] {
  const node = new Set(lines.map((line) => line.keyword.toLowerCase()));
  const counts = new Map<string, Collocate>();
  const add = (raw: string, side: "left" | "right") => {
    const word = raw.toLowerCase();
    if (
      node.has(word) ||
      (excludeStopwords &&
        STOPWORDS.has(word.replace("’", "'").replaceAll("'", "")))
    ) {
      return;
    }
    const entry = counts.get(word) ?? { word, left: 0, right: 0, total: 0 };
    entry[side]++;
    entry.total++;
    counts.set(word, entry);
  };
  for (const line of lines) {
    for (const word of line.leftWords) {
      add(word, "left");
    }
    for (const word of line.rightWords) {
      add(word, "right");
    }
  }
  return [...counts.values()].sort(
    (a, b) => b.total - a.total || a.word.localeCompare(b.word)
  );
}

function csvCell(value: string | number): string {
  return `"${String(value).replaceAll('"', '""')}"`;
}

/** The concordance as CSV (RFC 4180 quoting), in the order given. */
export function concordanceCsv(
  lines: readonly ConcordanceLine[],
  head: readonly [string, string, string, string]
): string {
  const rows = lines.map((line, i) => [
    i + 1,
    line.left,
    line.keyword,
    line.right,
  ]);
  return [head, ...rows]
    .map((cells) => cells.map(csvCell).join(","))
    .join("\r\n");
}
